import * as React from "react";
import { isRenderable, type Element, type Providers } from ".";

/**
 * Returns a component wrapper that wraps the component with all of the `parents`.
 *
 * @param parents The parents to wrap the child in, from outer to inner, either as just the component or a tuple of the component and its props.
 * Must be able to take a `children` prop.
 *
 * @example
 * const wrapper = wrap(ParentOne, [ParentTwo, { prop: "Something" }]);
 *
 * // is the same as writing
 * const wrapper = <Props extends {}>(Component: React.ComponentType<Props>, props?: Props) => <ParentOne>
 *   <ParentTwo prop="Something">
 *     <Component {...props} />
 *   </ParentTwo>
 * </ParentOne>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic type
export function wrap<TParents extends Record<string, any>[]>(
  ...parents: Providers<TParents>
) {
  function wrapper(child: Element): React.JSX.Element;
  function wrapper<TProps extends {}>(
    child: React.ComponentType<TProps>,
    props: TProps,
  ): React.JSX.Element;
  function wrapper<TProps extends {}>(
    child: React.ComponentType<TProps> | Element,
    props?: TProps,
  ) {
    return parents.reduceRight(
      (acc, cur) => {
        if (Array.isArray(cur)) {
          return React.createElement(cur[0], cur[1], acc);
        }

        return React.createElement(cur, null, acc);
      },
      isRenderable(child) ? child : React.createElement(child, props),
    );
  }

  return wrapper;
}
